import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { useCreateCollection } from '../../hooks/useCollections';
import { DEFAULT_VISIBILITY } from '../../lib/visibility';

const MAX_ID_LENGTH = 128;
const MAX_DESCRIPTION_LENGTH = 255;

// Toda regra carrega mensagem própria em português. Sem isso, `max` caía no
// texto default do zod, em inglês, no meio de mensagens traduzidas — foi a
// inconsistência que a QA viu ao digitar um id longo.
const schema = z.object({
  collection_id: z
    .string()
    .min(1, 'Obrigatório')
    .max(MAX_ID_LENGTH, `No máximo ${MAX_ID_LENGTH} caracteres`)
    .regex(/^[a-z0-9_-]+$/, 'Apenas letras minúsculas, números, _ e -'),
  description: z
    .string()
    .max(MAX_DESCRIPTION_LENGTH, `No máximo ${MAX_DESCRIPTION_LENGTH} caracteres`)
    .optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateCollectionModal({ open, onOpenChange }: Props) {
  const { mutateAsync, isPending } = useCreateCollection();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { description: '' },
  });

  async function onSubmit(values: FormValues) {
    await mutateAsync({
      collection_id: values.collection_id,
      description: values.description ?? '',
      default_visibility: DEFAULT_VISIBILITY,
    });
    reset();
    onOpenChange(false);
  }

  // O formulário vive neste componente, que continua montado com o diálogo
  // fechado, então fechar sem criar deixava os campos preenchidos esperando na
  // próxima abertura. Limpar aqui cobre Cancelar, Esc e clique fora.
  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nova coleção</DialogTitle>
          <DialogDescription>
            Uma coleção agrupa documentos indexados juntos. O assistente escolhe qual consultar pela
            descrição.
          </DialogDescription>
        </DialogHeader>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises -- handleSubmit wraps async handler */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pt-1">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="collection_id">
              ID da coleção
            </label>
            <Input id="collection_id" placeholder="ex: ppa_2024" {...register('collection_id')} />
            {errors.collection_id ? (
              <p className="text-destructive text-xs">{errors.collection_id.message}</p>
            ) : (
              <p className="text-muted-foreground text-xs">Identificador permanente da coleção.</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="description">
              Descrição
            </label>
            <Input id="description" placeholder="Opcional" {...register('description')} />
            <p className="text-muted-foreground text-xs">
              Ajuda o assistente a escolher esta coleção ao responder.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Criando…' : 'Criar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
